import React, { useState } from "react";
import LocationIcon from "../../assets/svg/location.svg";
import PhoneIcon from "../../assets/svg/phone.svg";
import EmailIcon from "../../assets/svg/email.svg";
import { submitContactDetails } from "../../Services/UserService";
import StatusPopup from "../../components/StatusPopup/StatusPopup";

const NAME_REGEX = /^[a-zA-Z\s]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const MAX_MESSAGE_LENGTH = 1000;

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
};

const validateForm = (data) => {
  const errors = {};
  const firstName = data.firstName.trim();
  const lastName = data.lastName.trim();
  const email = data.email.trim();
  const phone = data.phone.trim();
  const message = data.message.trim();

  if (!firstName) {
    errors.firstName = "First name is required";
  } else if (firstName.length < 2) {
    errors.firstName = "First name must be at least 2 characters";
  } else if (firstName.length > 50) {
    errors.firstName = "First name cannot exceed 50 characters";
  } else if (!NAME_REGEX.test(firstName)) {
    errors.firstName = "First name can only contain letters";
  }

  if (!lastName) {
    errors.lastName = "Last name is required";
  } else if (lastName.length < 2) {
    errors.lastName = "Last name must be at least 2 characters";
  } else if (lastName.length > 50) {
    errors.lastName = "Last name cannot exceed 50 characters";
  } else if (!NAME_REGEX.test(lastName)) {
    errors.lastName = "Last name can only contain letters";
  }

  if (!email) {
    errors.email = "Email is required";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Please enter a valid email address";
  } else if (email.length > 100) {
    errors.email = "Email cannot exceed 100 characters";
  }

  if (!phone) {
    errors.phone = "Phone number is required";
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = "Please enter a valid 10-digit mobile number";
  }

  if (!message) {
    errors.message = "Message is required";
  } else if (message.length < 10) {
    errors.message = "Message must be at least 10 characters";
  } else if (message.length > MAX_MESSAGE_LENGTH) {
    errors.message = `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`;
  }

  return errors;
};

const Contact = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusPopup, setStatusPopup] = useState({ show: false, message: "", type: "" });

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    let nextValue = value;

    if (id === "phone") {
      nextValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (id === "firstName" || id === "lastName") {
      nextValue = value.replace(/[^a-zA-Z\s]/g, "").slice(0, 50);
    }

    if (id === "message") {
      nextValue = value.slice(0, MAX_MESSAGE_LENGTH);
    }

    setFormData((prevData) => ({
      ...prevData,
      [id]: nextValue,
    }));
    clearFieldError(id);
  };

  const handleBlur = (e) => {
    const { id } = e.target;
    const fieldErrors = validateForm(formData);
    if (fieldErrors[id]) {
      setErrors((prev) => ({ ...prev, [id]: fieldErrors[id] }));
    }
  };

  const scrollToFirstError = (validationErrors) => {
    const firstErrorField = Object.keys(validationErrors)[0];
    const element = document.getElementById(firstErrorField);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      message: formData.message.trim(),
    };

    try {
      const response = await submitContactDetails(payload);
      if (response.status) {
        setStatusPopup({
          show: true,
          message: response.message || "Your message has been sent successfully!",
          type: "success",
        });
        setFormData(initialFormData);
      } else {
        setStatusPopup({
          show: true,
          message: response.message || "Failed to submit contact details.",
          type: "error",
        });
      }
    } catch (error) {
      setStatusPopup({
        show: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "An error occurred. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="contact-banner text-end">
              <h2 className={"fs_48 fw_700 lh-1-5 mb-0 position-relative z-2"}>Contact Us</h2>
            </div>
          </div>

          <div className="col-md-4">
            <div className="contact-details pt_68">
              <h3 className={"fs_36 fw_700 lh-1 text-color mb_18"}>Let's talk with us</h3>
              <p className={"fs_16 lh-1-5 text-color fw_400 mb_48"}>
                Questions, comments, or suggestions? Simply fill in the form and we’ll be in touch shortly.
              </p>

              <ul className="contact-info">
                <li>
                  <a
                    href="https://maps.google.com/?q=Near+sai+baba+chowk,+Panipat,+Haryana+132103"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img className="img-fluid icon-coffee" src={LocationIcon} alt="Location" />
                    <span>Near sai baba chowk, Panipat, Haryana 132103.</span>
                  </a>
                </li>

                <li>
                  <a href="tel:+918950500530">
                    <img className="img-fluid" src={PhoneIcon} alt="Phone icon" />
                    <span>+91 89505 00530</span>
                  </a>
                </li>

                <li>
                  <a href="mailto:support@moolchandji.com">
                    <img className="img-fluid icon-coffee" src={EmailIcon} alt="email icon" />
                    <span>support@moolchandji.com</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-md-8">
            <form onSubmit={handleSubmit} className={"contact-form"} noValidate>
              <div className="row g-4">
                <div className="col-lg-6">
                  <div className={"form-input"}>
                    <label htmlFor="firstName" className="form-label">
                      First name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                      id="firstName"
                      name="firstName"
                      placeholder="Enter your first name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={50}
                      autoComplete="given-name"
                      disabled={isSubmitting}
                    />
                    {errors.firstName && (
                      <div className="text-danger mt-2 fs_14">{errors.firstName}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className={"form-input"}>
                    <label htmlFor="lastName" className="form-label">
                      Last name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                      id="lastName"
                      name="lastName"
                      placeholder="Enter your last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={50}
                      autoComplete="family-name"
                      disabled={isSubmitting}
                    />
                    {errors.lastName && (
                      <div className="text-danger mt-2 fs_14">{errors.lastName}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className={"form-input"}>
                    <label htmlFor="email" className="form-label">
                      E-Mail <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? "is-invalid" : ""}`}
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={100}
                      autoComplete="email"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <div className="text-danger mt-2 fs_14">{errors.email}</div>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className={"form-input"}>
                    <label htmlFor="phone" className="form-label">
                      Phone number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                      id="phone"
                      name="phone"
                      placeholder="Enter 10-digit mobile number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={10}
                      inputMode="numeric"
                      autoComplete="tel"
                      disabled={isSubmitting}
                    />
                    {errors.phone && (
                      <div className="text-danger mt-2 fs_14">{errors.phone}</div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className={"form-input"}>
                    <label htmlFor="message" className="form-label">
                      Message <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${errors.message ? "is-invalid" : ""}`}
                      name="message"
                      id="message"
                      cols="30"
                      rows="8"
                      placeholder="Write your message.."
                      value={formData.message}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={MAX_MESSAGE_LENGTH}
                      disabled={isSubmitting}
                    />
                    <div className="d-flex justify-content-between align-items-start mt-2">
                      {errors.message ? (
                        <div className="text-danger fs_14">{errors.message}</div>
                      ) : (
                        <span />
                      )}
                      <span className="fs_14 text-muted">
                        {formData.message.length}/{MAX_MESSAGE_LENGTH}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className={"text-end"}>
                    <button
                      type="submit"
                      className={"button btn-solid"}
                      disabled={isSubmitting}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        opacity: isSubmitting ? 0.6 : 1,
                      }}
                    >
                      {isSubmitting ? "Sending..." : "Send"}
                    </button>
                  </div>
                </div>

                <StatusPopup
                  type={statusPopup.type}
                  show={statusPopup.show}
                  message={statusPopup.message}
                  onClose={() => setStatusPopup({ show: false, message: "", type: "" })}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
